// A lead sheet that OPENS in the bass clef writes its intro for the piano:
// McCoy Tyner's figure on Aisha, the bass lines of A Night in Tunisia,
// Chameleon, Africa, Splanky. The head starts where the clef changes to
// treble. The Score model keeps one clef per staff, so an import that left
// that passage in the lead part drew it in treble clef far below the staff and
// played it on the lead instrument (user report, Oct 2026: "make the intro a
// piano part, then it goes to a regular head that can be a tenor").
//
// This moves the passage into a Piano part (comp) that plays it note for note,
// in the bass clef: a figure like Aisha's IS the pianist's left hand. It
// writes no bass part, because the chart writes none ("I was expecting not to
// have a bass part at all"). The band already plays an authored comp part
// (partPlayback.ts), a part owns only the bars it writes, and the band makes
// everything else from the chord symbols: over an intro with none (Aisha) the
// piano plays alone, and from the head the generated comp, bass and drums take
// over. The lead part rests until the clef change and keeps the head.
//
// Pure: callers pass the clef changes the importer read (MusicXML writes them
// mid-measure, between notes); nothing here reads XML.

import { addF, compareF, durationQuarters, frac, mulF, type Fraction } from './duration';
import { slotsToItems } from './convert';
import { midiOf } from './pitch';
import type {
  Clef,
  NoteEvent,
  NoteHead,
  Part,
  PartMeasure,
  Score,
  Spanner,
  Voice,
  VoiceItem,
} from './types';

/** One clef taking effect on one staff, at a position in voice time. */
export interface StaffClefChange {
  staff: number;
  measureIndex: number;
  /** Quarter notes from the start of the measure's written content. */
  offset: Fraction;
  clef: Clef;
}

/** Where a lead sheet's bass-clef intro ends and the head begins. */
export interface BassClefIntro {
  /** The measure the head starts in, and where in it (quarter notes). */
  measureIndex: number;
  offset: Fraction;
  /** The clef the intro is written in, and the one the head is written in. */
  introClef: Clef;
  headClef: Clef;
}

/** A longer passage than this is not an intro; the importer leaves it alone. */
export const MAX_BASS_CLEF_INTRO_BARS = 16;

const TREBLE_CLEFS: readonly Clef[] = ['treble', 'treble8vb'];

type Position = { measureIndex: number; offset: Fraction };

function comparePosition(a: Position, b: Position): number {
  return a.measureIndex - b.measureIndex || compareF(a.offset, b.offset);
}

function eventsOf(items: readonly VoiceItem[]): NoteEvent[] {
  return items.flatMap((item) => (item.kind === 'tuplet' ? eventsOf(item.items) : [item]));
}

function sounds(event: NoteEvent): boolean {
  return event.kind === 'note' && event.notes.length > 0 && !event.slash;
}

/** Every event of a measure with its onset (quarter notes), tuplets resolved. */
function timedEvents(measure: PartMeasure): { event: NoteEvent; start: Fraction }[] {
  const out: { event: NoteEvent; start: Fraction }[] = [];
  const walk = (items: readonly VoiceItem[], factor: Fraction, start: Fraction): Fraction => {
    let position = start;
    for (const item of items) {
      if (item.kind === 'tuplet') {
        position = walk(item.items, mulF(factor, frac(item.normal, item.actual)), position);
        continue;
      }
      out.push({ event: item, start: position });
      if (!item.grace) position = addF(position, mulF(durationQuarters(item.duration), factor));
    }
    return position;
  };
  for (const voice of measure.voices) walk(voice.items, frac(1), frac(0));
  return out;
}

/** The written length of an item list (graces take no time). */
function itemsLength(items: readonly VoiceItem[], factor: Fraction = frac(1)): Fraction {
  let total = frac(0);
  for (const item of items) {
    if (item.kind === 'tuplet') {
      total = addF(total, itemsLength(item.items, mulF(factor, frac(item.normal, item.actual))));
    } else if (!item.grace) {
      total = addF(total, mulF(durationQuarters(item.duration), factor));
    }
  }
  return total;
}

/**
 * The intro this score's lead sheet opens with, or null when it has none.
 *
 * It must be a lead sheet (one part, one staff, chord symbols) that opens in
 * the bass clef and changes to a treble clef within MAX_BASS_CLEF_INTRO_BARS,
 * with written notes on both sides of the change. Sung words in the passage
 * make it the melody, not an intro, and so does a part already given a band
 * role.
 */
export function bassClefIntroOf(
  score: Score,
  changes: readonly StaffClefChange[],
): BassClefIntro | null {
  if (score.parts.length !== 1) return null;
  const part = score.parts[0];
  if (part.staves.length !== 1) return null;
  if (part.role !== undefined && part.role !== 'lead') return null;
  if (!score.form.some((measure) => measure.harmony.some((chord) => chord.root))) return null;

  const onStaff = changes
    .filter((change) => change.staff === 0)
    .sort((a, b) => comparePosition(a, b));
  const atStart = onStaff.filter(
    (change) => change.measureIndex === 0 && compareF(change.offset, frac(0)) <= 0,
  );
  const introClef = atStart[atStart.length - 1]?.clef;
  if (introClef !== 'bass') return null;
  const head = onStaff.find(
    (change) =>
      (change.measureIndex > 0 || compareF(change.offset, frac(0)) > 0) &&
      change.clef !== introClef,
  );
  if (!head || !TREBLE_CLEFS.includes(head.clef)) return null;
  if (head.measureIndex > MAX_BASS_CLEF_INTRO_BARS) return null;

  let introNotes = 0;
  let headNotes = 0;
  for (const [measureIndex, measure] of part.measures.entries()) {
    for (const { event, start } of timedEvents(measure)) {
      if (!sounds(event)) continue;
      if (comparePosition({ measureIndex, offset: start }, head) < 0) {
        if (event.lyrics?.some((syllable) => syllable?.text)) return null;
        introNotes += 1;
      } else {
        headNotes += 1;
      }
    }
  }
  if (introNotes === 0 || headNotes === 0) return null;
  return { measureIndex: head.measureIndex, offset: head.offset, introClef, headClef: head.clef };
}

/** Rests filling `quarters` of written time, with deterministic ids. */
function gapRests(quarters: Fraction, idBase: string): VoiceItem[] {
  if (compareF(quarters, frac(0)) <= 0) return [];
  return slotsToItems([{ quarters, idBase }]);
}

function asRest(event: NoteEvent, id: string): NoteEvent {
  return { id, kind: 'rest', duration: event.duration, notes: [] };
}

/**
 * Cut one voice of the measure the head starts in: `before` keeps what sounds
 * before `cut` and rests after it; `after` rests before it and keeps the rest.
 * An event belongs to the side its onset falls on. A tuplet the cut runs
 * through keeps its bracket on both sides, the other side's members as rests.
 */
function cutVoice(
  voice: Voice,
  cut: Fraction,
  suffix: string,
): { before: VoiceItem[]; after: VoiceItem[] } {
  const before: VoiceItem[] = [];
  const after: VoiceItem[] = [];
  let leading = frac(0);
  let trailing = frac(0);
  let position = frac(0);
  for (const item of voice.items) {
    const length = itemsLength([item]);
    const end = addF(position, length);
    if (compareF(position, cut) >= 0) {
      after.push(item);
      trailing = addF(trailing, length);
    } else if (item.kind === 'tuplet' && compareF(end, cut) > 0) {
      // The cut runs through the bracket: split it member by member.
      const factor = frac(item.normal, item.actual);
      let at = position;
      const sides = item.items.map((member) => {
        const onset = at;
        if (member.kind !== 'tuplet' && !member.grace) {
          at = addF(at, mulF(durationQuarters(member.duration), factor));
        } else if (member.kind === 'tuplet') {
          at = addF(at, itemsLength([member], factor));
        }
        return { member, early: compareF(onset, cut) < 0 };
      });
      const restOf = (member: VoiceItem): VoiceItem =>
        member.kind === 'tuplet'
          ? { ...member, id: `${member.id}:rest`, items: member.items.map(restOf) }
          : asRest(member, `${member.id}:rest`);
      after.push(...gapRests(leading, `${voice.id}:intro-rest`), {
        ...item,
        items: sides.map(({ member, early }) => (early ? restOf(member) : member)),
      });
      leading = frac(0);
      before.push({
        ...item,
        id: `${item.id}${suffix}`,
        items: sides.map(({ member, early }) => (early ? member : restOf(member))),
      });
    } else {
      before.push(item);
      leading = addF(leading, length);
    }
    position = end;
  }
  if (compareF(leading, frac(0)) > 0) after.unshift(...gapRests(leading, `${voice.id}:intro-rest`));
  before.push(...gapRests(trailing, `${voice.id}${suffix}:head-rest`));
  return { before, after };
}

function hasSound(measure: PartMeasure): boolean {
  return measure.voices.some((voice) => eventsOf(voice.items).some((e) => sounds(e) && !e.grace));
}

/** A measure with nothing to play is EMPTY, so it claims no band channel. */
function emptyUnlessSounding(measure: PartMeasure): PartMeasure {
  if (hasSound(measure)) return measure;
  const { playback: _playback, ...rest } = measure;
  return { ...rest, voices: [] };
}

function mapEvents(items: readonly VoiceItem[], fn: (event: NoteEvent) => NoteEvent): VoiceItem[] {
  return items.map((item) =>
    item.kind === 'tuplet' ? { ...item, items: mapEvents(item.items, fn) } : fn(item),
  );
}

function dropTieHalf(head: NoteHead, half: 'start' | 'stop'): NoteHead {
  const keep =
    head.tie === 'both'
      ? half === 'start'
        ? 'stop'
        : 'start'
      : head.tie === half
        ? undefined
        : head.tie;
  const { tie: _tie, ...rest } = head;
  return keep ? { ...rest, tie: keep } : rest;
}

/**
 * Drop tie starts that no longer reach a note: the cut leaves the intro's last
 * note tied into a rest when it was tied into the head. Voices pair by index
 * across barlines; graces are skipped, rests and empty bars break a tie.
 */
function repairTieStarts(part: Part): Part {
  const lanes = Math.max(0, ...part.measures.map((measure) => measure.voices.length));
  const dangling = new Set<string>();
  for (let lane = 0; lane < lanes; lane += 1) {
    const stream: (NoteEvent | null)[] = [];
    for (const measure of part.measures) {
      const voice = measure.voices[lane];
      if (!voice) {
        stream.push(null);
        continue;
      }
      stream.push(...eventsOf(voice.items).filter((event) => !event.grace));
    }
    stream.forEach((event, index) => {
      if (!event || event.kind !== 'note') return;
      const next = stream[index + 1];
      for (const head of event.notes) {
        if (head.tie !== 'start' && head.tie !== 'both') continue;
        const reached =
          next?.kind === 'note' &&
          next.notes.some(
            (other) =>
              midiOf(other.pitch) === midiOf(head.pitch) &&
              (other.tie === 'stop' || other.tie === 'both'),
          );
        if (!reached) dangling.add(`${event.id}\u0000${midiOf(head.pitch)}`);
      }
    });
  }
  if (dangling.size === 0) return part;
  return {
    ...part,
    measures: part.measures.map((measure) => ({
      ...measure,
      voices: measure.voices.map((voice) => ({
        ...voice,
        items: mapEvents(voice.items, (event) =>
          event.notes.some((head) => dangling.has(`${event.id}\u0000${midiOf(head.pitch)}`))
            ? {
                ...event,
                notes: event.notes.map((head) =>
                  dangling.has(`${event.id}\u0000${midiOf(head.pitch)}`)
                    ? dropTieHalf(head, 'start')
                    : head,
                ),
              }
            : event,
        ),
      })),
    })),
  };
}

const GENERIC_PART_NAME = /^\s*(Part \d+)?\s*$/;

/**
 * Give a lead sheet's bass-clef intro to the piano: [Lead, Piano]. Returns the
 * score itself when it has no such intro (bassClefIntroOf). Event ids move
 * with their events, so slurs inside the intro stay valid; a spanner reaching
 * from the intro into the head is dropped.
 */
export function splitBassClefIntro(score: Score, changes: readonly StaffClefChange[]): Score {
  const intro = bassClefIntroOf(score, changes);
  if (!intro) return score;
  const source = score.parts[0];
  const { measureIndex: cutBar, offset: cut } = intro;
  const cutAtBarline = compareF(cut, frac(0)) <= 0;

  const lead: PartMeasure[] = [];
  const piano: PartMeasure[] = [];
  source.measures.forEach((measure, index) => {
    const pianoId = `${measure.id}:piano`;
    if (index < cutBar) {
      lead.push({ id: measure.id, voices: [] });
      piano.push({
        ...measure,
        id: pianoId,
        voices: measure.voices.map((voice) => ({ ...voice, id: `${voice.id}:piano` })),
      });
    } else if (index === cutBar && !cutAtBarline) {
      const cuts = measure.voices.map((voice) => ({ voice, ...cutVoice(voice, cut, ':piano') }));
      lead.push({
        ...measure,
        voices: cuts.map(({ voice, after }) => ({ ...voice, items: after })),
      });
      piano.push({
        ...measure,
        id: pianoId,
        voices: cuts.map(({ voice, before }) => ({
          ...voice,
          id: `${voice.id}:piano`,
          items: before,
        })),
      });
    } else {
      lead.push(measure);
      piano.push({ id: pianoId, voices: [] });
    }
  });

  // Rests before the head's first note: a stop tie there joins nothing now.
  const leadMeasures = lead.map((measure, index) =>
    index === cutBar ? emptyUnlessSounding(measure) : measure,
  );
  const leadLanes = Math.max(0, ...leadMeasures.map((measure) => measure.voices.length));
  for (let lane = 0; lane < leadLanes; lane += 1) {
    const at = leadMeasures.findIndex(
      (measure, index) =>
        index >= cutBar && eventsOf(measure.voices[lane]?.items ?? []).some(sounds),
    );
    if (at < 0) continue;
    const measure = leadMeasures[at];
    const voice = measure.voices[lane];
    const first = eventsOf(voice.items).find(sounds)!;
    if (!first.notes.some((head) => head.tie === 'stop' || head.tie === 'both')) continue;
    const voices = [...measure.voices];
    voices[lane] = {
      ...voice,
      items: mapEvents(voice.items, (event) =>
        event === first
          ? { ...event, notes: event.notes.map((head) => dropTieHalf(head, 'stop')) }
          : event,
      ),
    };
    leadMeasures[at] = { ...measure, voices };
  }

  const pianoPart = repairTieStarts({
    id: `${source.id}:piano`,
    name: 'Piano',
    shortName: 'Pno.',
    instrument: 'piano',
    role: 'comp',
    staves: [{ ...source.staves[0], id: `${source.staves[0].id}:piano`, clef: intro.introClef }],
    measures: piano.map(emptyUnlessSounding),
  });

  const leadPart: Part = {
    ...source,
    name: GENERIC_PART_NAME.test(source.name) ? 'Lead' : source.name,
    role: 'lead',
    staves: [{ ...source.staves[0], clef: intro.headClef }],
    measures: leadMeasures,
  };

  const partOf = new Map<string, string>();
  for (const part of [leadPart, pianoPart]) {
    for (const measure of part.measures) {
      for (const voice of measure.voices) {
        for (const event of eventsOf(voice.items)) partOf.set(event.id, part.id);
      }
    }
  }
  const spanners = score.spanners?.filter((spanner: Spanner) => {
    const from = partOf.get(spanner.fromEventId);
    return from !== undefined && from === partOf.get(spanner.toEventId);
  });

  return {
    ...score,
    parts: [leadPart, pianoPart],
    ...(spanners ? { spanners } : {}),
  };
}
